// src/encounter/workspace-timeline.tsx
//
// PROD-2743 · frame v2 (DARK). The workspace's Timeline tab: the care-pathway timeline, moved from
// the right drawer's Timeline tab — and FED this time. The drawer's copy was never handed its
// `activities`, so it could only ever say "will appear here once the feed is available", while the
// pre-encounter rail beside it read the real feed (PROD-738). A tab that can only show that line is
// a dead tab, so this one reads the SAME feed through the SAME hook and query key the pre-encounter
// rail uses (`useAppointmentWorkflow`, GET workflows/appointments/{id}/history — never the route
// without `/history`, which RUNS the workflow), so it is usually a cache hit.
//
// The four outcomes stay four distinct lines (review #314: reading any of the other three as "no
// care-pathway activity" is a clinical misread), with the DS CarePathwayRail's own wording:
//   loading · failed (with Retry) · no appointment to read for · read, and empty.
// A populated feed renders through the existing CarePathwayTimeline, unchanged.
//
// It owns its read (like CarePathwayRailContainer), so the page grows no hook: only the v2 Timeline
// tab mounts it, and only while that tab is open.
import { useMemo } from 'react';
import { Button, EmptyState } from '@scripsteam/scrips-signal-ds';
import { QuietLoading } from '@/shared/ui/components/quiet-loading';
import { useDesignV2 } from './lib/design-v2';
import { CarePathwayTimeline } from './care-pathway-timeline';
import { toCarePathwayActivities } from './lib/care-pathway';
import { useAppointmentWorkflow } from './services/use-appointment-workflow';

const NOTICE = 'm-0 text-size-sm text-text-secondary';

/** `inline` (the Chart tab's Timeline section): the section sits above the chart, so a failed or empty read is one quiet line
 *  (the DS EmptyState would take a screen), and the body takes its own height instead of scrolling inside the tab. */
export function WorkspaceTimeline({ appointmentId, inline = false }: { appointmentId: string | undefined; inline?: boolean }) {
  const query = useAppointmentWorkflow({ appointmentId });
  const activities = useMemo(() => toCarePathwayActivities(query.data?.data), [query.data]);
  // Design sheet v1.1 §1 States (switch ON only): failed and empty use the DS EmptyState. This tab
  // only mounts under frame v2, so ON is its normal case; the OFF lines stay for any other host.
  const designV2 = useDesignV2() && !inline;

  let body;
  if (!appointmentId) {
    body = <p className={NOTICE}>Care pathway isn’t available yet.</p>;
  } else if (query.isLoading) {
    body = <QuietLoading label="Loading care pathway…" />;
  } else if (query.isError && designV2) {
    body = (
      <EmptyState
        icon="warning"
        // The DS CarePathwayRail's own wording (review #314 keeps the four outcomes distinct).
        title="Care pathway couldn’t be loaded."
        ctaLabel="Retry"
        onCtaClick={() => void query.refetch()}
      />
    );
  } else if (query.isError) {
    body = (
      <p className={`${NOTICE} inline-flex items-center gap-2`}>
        Care pathway couldn’t be loaded.
        <Button variant="link" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </p>
    );
  } else if (activities.length === 0) {
    body = designV2 ? (
      <EmptyState icon="clock" title="No care-pathway activity for this appointment yet." />
    ) : (
      <p className={NOTICE}>No care-pathway activity for this appointment yet.</p>
    );
  } else {
    body = <CarePathwayTimeline activities={activities} />;
  }
  return (
    <div data-slot="workspace-timeline" className={inline ? undefined : 'flex-1 min-h-0 overflow-y-auto p-4'}>
      {body}
    </div>
  );
}
